import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { OrderListQuery } from 'ish-core/models/order-list-query/order-list-query.model';
import { Order } from 'ish-core/models/order/order.model';
import { PagingInfo } from 'ish-core/models/paging-info/paging-info.model';
import { setErrorOn, setLoadingOn, unsetLoadingAndErrorOn } from 'ish-core/utils/ngrx-creators';

import {
  createMultipleShippingOrder,
  createMultipleShippingOrderSuccess,
  createOrder,
  createOrderFail,
  createOrderSuccess,
  loadCustomOrders,
  loadCustomOrdersSuccess,
  loadOrder,
  loadOrderFail,
  loadOrderSuccess,
  loadOrders,
  loadOrdersFail,
  loadOrdersSuccess,
  resetOrderErrors,
  resetOrders,
  selectOrder,
} from './orders.actions';

export const orderAdapter = createEntityAdapter<Order>({
  selectId: order => order.id,
});

export interface OrdersState extends EntityState<Order> {
  loading: boolean;
  selected: string;
  query: OrderListQuery;
  error: HttpError;
  paging: PagingInfo;
  moreAvailable: boolean; // DIAS CUSTOMIZATION
}

const initialState: OrdersState = orderAdapter.getInitialState({
  loading: false,
  selected: undefined,
  query: undefined,
  error: undefined,
  paging: undefined,
  moreAvailable: true,
});

export const ordersReducer = createReducer(
  initialState,
  setLoadingOn(createOrder, loadOrder, loadOrders, createMultipleShippingOrder, loadCustomOrders),
  unsetLoadingAndErrorOn(
    createOrderSuccess,
    loadOrderSuccess,
    loadOrdersSuccess,
    loadCustomOrdersSuccess,
    createMultipleShippingOrderSuccess
  ),
  setErrorOn(loadOrdersFail, loadOrderFail, createOrderFail),
  on(selectOrder, (state, action): OrdersState => ({
    ...state,
    selected: action.payload.orderId,
  })),

  on(createOrderSuccess, loadOrderSuccess, createMultipleShippingOrderSuccess, (state, action) => {
    const { order } = action.payload;

    return order.id
      ? {
          ...orderAdapter.upsertOne(order, state),
          selected: order.id,
        }
      : state;
  }),
  /* DIAS CUSTOMIZATION START */
  on(loadOrdersSuccess, (state, action) => {
    const { orders, query } = action.payload;
    const newState = { ...state, query };

    const updatedOrders = orders.map((order, index) => ({
      ...order,
      paginationPosition: (query.offset ? query.offset : 0) + index,
    }));

    return {
      ...(query.offset ? orderAdapter.addMany(updatedOrders, newState) : orderAdapter.setAll(updatedOrders, newState)),
      paging: action.payload.paging,
    };
    //Commented out while migrating 8.0.0
    //    const { orders, query, allRetrieved } = action.payload;
    // const newState = { ...state, query, moreAvailable: !allRetrieved };
    // if (!query?.offset) {
    //   return orderAdapter.setAll(orders, newState);
    // } else {
    //   return orderAdapter.addMany(orders, newState);
    //}
  }),
  on(loadCustomOrdersSuccess, (state, action) => {
    const { orders, query, allRetrieved } = action.payload;
    const newState = { ...state, query, moreAvailable: !allRetrieved };

    // The order history renders the page of orders it filters by this position, so an order without
    // one is filtered out and the list stays empty even though the orders arrived. `loadOrdersSuccess`
    // above sets it the same way.
    const updatedOrders = orders.map((order, index) => ({
      ...order,
      paginationPosition: (query.offset ? query.offset : 0) + index,
    }));

    return query.offset ? orderAdapter.addMany(updatedOrders, newState) : orderAdapter.setAll(updatedOrders, newState);
  }),
  on(resetOrders, state => ({
    ...orderAdapter.removeAll(state),
  })),
  /* DIAS CUSTOMIZATION END */
  on(resetOrderErrors, (state): OrdersState => ({
    ...state,
    error: undefined,
  }))
);
